import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Stack } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { GetEventSettingsDto } from '@/types/dtos/events/response/get-event-settings.dto';
import MediaSettingsSwitchRow from '@/components/media/media-settings-switch-row';
import {
  PlaylistPermissionCreateIcon,
  PlaylistPermissionPairIcon,
  PlaylistPermissionReorderIcon,
} from '@/components/media/playlist-media-permission-icons';
import {
  type MediaSettingsConfirmKey,
  mediaSettingsConfirmMessageKey,
} from '@/components/media/media-settings-confirm.types';
import { useTranslations } from '@/i18n/use-translations';
import { usePatchFlowMediaSettingsMutation } from '@/state/mutations/flows/use-patch-flow-media-settings';
import { useNotifications } from '@/hooks/use-notifications';
import { getObjectId } from '@/helpers/object.id.helper';
import type { FlowMediaSettingsDto } from '@/types/dtos/media/media-settings.dto';

type HostDefaultsSwitchField = 'allowSelfCreate' | 'allowReorder' | 'allowScopeOverride';

type Pending = {
  field: HostDefaultsSwitchField;
  patch: Partial<FlowMediaSettingsDto>;
  confirmKey: MediaSettingsConfirmKey;
};

const HOST_FIELD_CONFIRM_KEYS: Record<HostDefaultsSwitchField, MediaSettingsConfirmKey> = {
  allowSelfCreate: 'hosts.allowSelfCreate.off',
  allowReorder: 'hosts.allowReorder.off',
  allowScopeOverride: 'hosts.allowScopeOverride.off',
};

function patchToHostDefaultsFields(patch: Partial<FlowMediaSettingsDto>): HostDefaultsSwitchField[] {
  const fields: HostDefaultsSwitchField[] = [];
  if (patch.hosts?.allowSelfCreate !== undefined) fields.push('allowSelfCreate');
  if (patch.hosts?.allowReorder !== undefined) fields.push('allowReorder');
  if (patch.hosts?.allowScopeOverride !== undefined) fields.push('allowScopeOverride');
  return fields;
}

export function FlowHostMediaDefaultsFields({
  flow,
  eventSettings,
  multiFlow = false,
}: {
  flow: FlowDto;
  eventSettings?: GetEventSettingsDto | null;
  multiFlow?: boolean;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const flowOriginId = flow.origin ? getObjectId(flow.origin) : flow.id;
  const caps = flow.mediaSettingsCapabilities;
  const stored = flow.mediaSettings ?? {};
  const eventHosts = eventSettings?.mediaSettings?.hosts;

  const [pending, setPending] = useState<Pending | null>(null);
  const [optimisticChecked, setOptimisticChecked] = useState<Partial<Record<HostDefaultsSwitchField, boolean>>>({});
  const [isResetting, setIsResetting] = useState(false);
  const { mutateAsync, isPending } = usePatchFlowMediaSettingsMutation();

  const clearOptimistic = (fields: HostDefaultsSwitchField[]) => {
    if (fields.length === 0) return;
    setOptimisticChecked((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of fields) {
        if (field in next) {
          delete next[field];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  const setOptimistic = (field: HostDefaultsSwitchField, value: boolean) => {
    setOptimisticChecked((prev) => ({ ...prev, [field]: value }));
  };

  const applyPatch = async (patch: Partial<FlowMediaSettingsDto>) => {
    const fields = patchToHostDefaultsFields(patch);
    try {
      await mutateAsync({ flowId: flowOriginId, patch });
      notification.notifySuccess(t('media.settings.saveSuccess'));
    } catch {
      notification.notifyError(t('media.settings.saveError'));
    } finally {
      clearOptimistic(fields);
    }
  };

  const cancelPending = () => {
    if (pending) {
      clearOptimistic([pending.field]);
    }
    setPending(null);
  };

  const confirmPending = () => {
    const current = pending;
    setPending(null);
    if (current) {
      void applyPatch(current.patch);
    }
  };

  const switchConfirm = (field: HostDefaultsSwitchField) => {
    if (pending?.field !== field) return null;
    return {
      message: t(mediaSettingsConfirmMessageKey(pending.confirmKey)),
      onCancel: cancelPending,
      onConfirm: confirmPending,
    };
  };

  const changeHostRef = useRef<(field: 'allowSelfCreate' | 'allowReorder', value: boolean) => void>(() => {});
  const changeScopeOverrideRef = useRef<(value: boolean) => void>(() => {});

  changeHostRef.current = (field: 'allowSelfCreate' | 'allowReorder', value: boolean) => {
    setOptimistic(field, value);
    const patch = { hosts: { [field]: value } };
    if (!value) {
      setPending({ field, patch, confirmKey: HOST_FIELD_CONFIRM_KEYS[field] });
    } else {
      void applyPatch(patch);
    }
  };

  changeScopeOverrideRef.current = (value: boolean) => {
    setOptimistic('allowScopeOverride', value);
    const patch = { hosts: { allowScopeOverride: value } };
    if (!value) {
      setPending({ field: 'allowScopeOverride', patch, confirmKey: HOST_FIELD_CONFIRM_KEYS.allowScopeOverride });
    } else {
      void applyPatch(patch);
    }
  };

  const onHostCreateChange = useCallback((value: boolean) => changeHostRef.current('allowSelfCreate', value), []);
  const onHostReorderChange = useCallback((value: boolean) => changeHostRef.current('allowReorder', value), []);
  const onScopeOverrideChange = useCallback((value: boolean) => changeScopeOverrideRef.current(value), []);

  const eventAllowFlowOverride = eventHosts?.allowFlowOverride ?? true;

  const hostCreateStored = stored.hosts?.allowSelfCreate ?? eventHosts?.allowSelfCreate ?? true;
  const hostReorderStored = stored.hosts?.allowReorder ?? eventHosts?.allowReorder ?? true;
  const scopeOverrideStored = stored.hosts?.allowScopeOverride ?? true;

  const canEditFlow = caps?.canEdit ?? false;
  const canEditFlowHostDefaults = canEditFlow && eventAllowFlowOverride;
  const canEditScopeOverrideFlag = canEditFlow && eventAllowFlowOverride;

  const hostCreateResolved = canEditFlowHostDefaults ? hostCreateStored : (eventHosts?.allowSelfCreate ?? true);
  const hostReorderResolved = canEditFlowHostDefaults ? hostReorderStored : (eventHosts?.allowReorder ?? true);

  const hostCreate = optimisticChecked.allowSelfCreate ?? hostCreateResolved;
  const hostReorder = optimisticChecked.allowReorder ?? hostReorderResolved;
  const scopeOverride = optimisticChecked.allowScopeOverride ?? scopeOverrideStored;

  const labels = useMemo(
    () => ({
      hostCreate: t('media.settings.hosts.permissionsCreate'),
      hostReorder: t('media.settings.hosts.permissionsReorder'),
      scopeOverride: t('media.settings.hosts.permissionsScopeOverride'),
    }),
    [t],
  );

  const flowHostOverrideTooltip = useMemo(() => t('media.settings.tooltip.disabled.flowHostOverride'), [t]);
  const hostDefaultsDisabledTooltip = !canEditFlowHostDefaults ? flowHostOverrideTooltip : undefined;
  const scopeOverrideDisabledTooltip = !canEditScopeOverrideFlag ? flowHostOverrideTooltip : undefined;

  const eventMedia = eventSettings?.mediaSettings;
  const canResetToEventDefaults = multiFlow && Boolean(eventMedia) && canEditFlow;

  const handleResetToEventDefaults = async () => {
    if (!eventMedia?.hosts) return;
    const patch: Partial<FlowMediaSettingsDto> = {
      hosts: {
        allowSelfCreate: eventMedia.hosts.allowSelfCreate,
        allowReorder: eventMedia.hosts.allowReorder,
        allowScopeOverride: eventMedia.hosts.allowFlowOverride,
      },
    };
    setOptimistic('allowSelfCreate', patch.hosts?.allowSelfCreate ?? true);
    setOptimistic('allowReorder', patch.hosts?.allowReorder ?? true);
    setOptimistic('allowScopeOverride', patch.hosts?.allowScopeOverride ?? true);
    setIsResetting(true);
    try {
      await mutateAsync({ flowId: flowOriginId, patch });
      notification.notifySuccess(t('media.settings.resetSuccess'));
    } catch {
      notification.notifyError(t('media.settings.resetError'));
      clearOptimistic(['allowSelfCreate', 'allowReorder', 'allowScopeOverride']);
    } finally {
      setIsResetting(false);
      clearOptimistic(['allowSelfCreate', 'allowReorder', 'allowScopeOverride']);
    }
  };

  return (
    <Stack spacing={1}>
      <MediaSettingsSwitchRow
        switchKey={`flow-allowSelfCreate-${hostCreate}`}
        label={labels.hostCreate}
        checked={hostCreate}
        disabled={!canEditFlowHostDefaults || isPending}
        tooltip={hostDefaultsDisabledTooltip}
        leadingIcon={<PlaylistPermissionCreateIcon />}
        confirm={switchConfirm('allowSelfCreate')}
        onChange={onHostCreateChange}
      />
      <MediaSettingsSwitchRow
        switchKey={`flow-allowReorder-${hostReorder}`}
        label={labels.hostReorder}
        checked={hostReorder}
        disabled={!canEditFlowHostDefaults || isPending}
        tooltip={hostDefaultsDisabledTooltip}
        leadingIcon={<PlaylistPermissionReorderIcon />}
        confirm={switchConfirm('allowReorder')}
        onChange={onHostReorderChange}
      />
      <MediaSettingsSwitchRow
        switchKey={`flow-allowScopeOverride-${scopeOverride}`}
        label={labels.scopeOverride}
        checked={scopeOverride}
        disabled={!canEditScopeOverrideFlag || isPending}
        tooltip={scopeOverrideDisabledTooltip}
        leadingIcon={<PlaylistPermissionPairIcon />}
        confirm={switchConfirm('allowScopeOverride')}
        onChange={onScopeOverrideChange}
      />

      {canResetToEventDefaults && (
        <Button
          size="small"
          color="inherit"
          startIcon={<RestartAltIcon fontSize="small" />}
          onClick={() => void handleResetToEventDefaults()}
          disabled={isPending || isResetting}
          sx={{
            'mt': 2,
            'px': 0,
            'minWidth': 0,
            'justifyContent': 'flex-start',
            'color': 'text.secondary',
            'textTransform': 'none',
            'fontSize': '0.8125rem',
            '&:hover': { backgroundColor: 'transparent', color: 'text.primary' },
          }}
        >
          {t('media.settings.flow.resetToEventDefaults', {
            create: eventHosts!.allowSelfCreate ? 'on' : 'off',
            reorder: eventHosts!.allowReorder ? 'on' : 'off',
          })}
        </Button>
      )}
    </Stack>
  );
}
